// src/components/HomeGate.js
//
// The "/" route's new brain. Replaces the previous
// Landing-or-ClientShell-with-Home ternary in App.js so a logged-in
// client's Home surface is CONSOLIDATED to /p/:joinCode (per design
// handoff Step 5) instead of duplicated at /.
//
// Branches:
//   loading         → minimal loader (no ClientShell/Home flash)
//   anonymous       → <Landing />
//   admin           → <Navigate to="/admin" replace />
//   provider        → <Navigate to="/provider" replace />
//   client + joinCode → <Navigate to="/p/:joinCode<?search><#hash>" replace />
//   client + no jc  → <ProviderPageUnavailable /> (graceful fallback,
//                     no redirect loop, no 404)
//
// Query params + hash on / are carried through to /p/:joinCode so
// deep-link intents (e.g. /?ref=X) survive the consolidation.

import React from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import Landing from './Landing';

export default function HomeGate() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();

  if (loading) return <MinimalLoader />;
  // Ivan's ruling (2026-08-25): avayble.app "/" is SOLELY the marketing
  // page in a browser — always reachable, no localStorage flag may
  // hijack it (the old av.hasSignedIn redirect made the homepage
  // unreachable on any browser that ever held a session; gone).
  // The installed PWA is the one context that never belongs here: the
  // manifest now starts installs at /login directly, and the
  // display-mode / navigator.standalone / ?source=pwa detection stays
  // for apps installed under the OLD start_url ("/?source=pwa") —
  // manifest changes don't retroactively update installed apps.
  if (!user) {
    const isInstalledApp =
      typeof window !== 'undefined' && (
        window.matchMedia?.('(display-mode: standalone)')?.matches ||
        window.matchMedia?.('(display-mode: fullscreen)')?.matches ||
        window.matchMedia?.('(display-mode: minimal-ui)')?.matches ||
        window.navigator?.standalone === true ||
        new URLSearchParams(location.search).get('source') === 'pwa'
      );
    return isInstalledApp ? <Navigate to="/login" replace /> : <Landing />;
  }
  // Admins are NOT auto-routed to /admin (2026-08-30) — the flag rides on
  // a working provider account; mission control is reached by URL.
  if (user.accountType === 'PROVIDER') return <Navigate to="/provider" replace />;

  // CLIENT branch.
  const joinCode = user.provider?.joinCode;
  if (joinCode) {
    const target = `/p/${joinCode}${location.search || ''}${location.hash || ''}`;
    return <Navigate to={target} replace />;
  }

  // Unassigned client — either a join request is awaiting the
  // provider's approval (say so), or they're a true orphan.
  return <UnassignedClientGate onSignOut={logout} providerBusinessName={user.provider?.businessName} />;
}

// Unassigned CLIENT home: check for a pending join request first
// (clientJoinMode 'approval' creates the account before the roster
// spot) and render the honest waiting screen; fall back to the
// original dead-end card for true orphans.
function UnassignedClientGate({ onSignOut, providerBusinessName }) {
  const [state, setState] = React.useState({ loading: true, pending: null });
  React.useEffect(() => {
    let cancelled = false;
    fetch('/api/provider-requests/client/status', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled) return;
        setState({ loading: false, pending: data?.hasPendingRequest ? data.request : null });
      })
      .catch(() => { if (!cancelled) setState({ loading: false, pending: null }); });
    return () => { cancelled = true; };
  }, []);

  if (state.loading) return <MinimalLoader />;
  if (state.pending) {
    const name = state.pending.provider?.businessName || 'Your provider';
    return (
      <div className="av-paper" style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 20px' }}>
        <div className="rounded-card border" style={{ maxWidth: 440, width: '100%', backgroundColor: 'var(--bg-elev)', borderColor: 'var(--line)', padding: '28px 24px', textAlign: 'center' }}>
          <div className="av-eyebrow text-accent-ink mb-2">Request sent</div>
          <h1 className="font-display text-ink" style={{ fontSize: 24, fontWeight: 500, letterSpacing: '-0.005em', lineHeight: 1.2 }}>
            Waiting on {name}
          </h1>
          <p className="text-sm text-ink-2 mt-3" style={{ lineHeight: 1.6 }}>
            They approve every new client personally. You'll get a text the moment they say
            yes — booking opens right then. Nothing else to do here.
          </p>
          <button
            type="button"
            onClick={async () => { try { if (typeof onSignOut === 'function') await onSignOut(); } catch { /* ignore */ } window.location.href = '/'; }}
            className="mt-5 text-sm text-ink-3 underline hover:text-ink-2"
            style={{ background: 'none', border: 0, cursor: 'pointer' }}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }
  return <ProviderPageUnavailable onSignOut={onSignOut} providerBusinessName={providerBusinessName} />;
}

// Small loader — same paper background as the app so there's no
// jarring flash between auth-check and destination.
function MinimalLoader() {
  return (
    <div className="av-paper" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div
        aria-hidden
        style={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          border: '2px solid var(--line)',
          borderTopColor: 'var(--accent)',
          animation: 'homegateSpin 700ms linear infinite',
        }}
      />
      <style>{`@keyframes homegateSpin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// Client is authenticated but their provider hasn't set a joinCode
// (or is deactivated). Rather than redirect-loop or 404, offer them
// two exits: sign out, or reach out to their provider directly.
function ProviderPageUnavailable({ onSignOut, providerBusinessName }) {
  const handleSignOut = async () => {
    try { if (typeof onSignOut === 'function') await onSignOut(); } catch { /* ignore */ }
    window.location.href = '/';
  };
  return (
    <div className="av-paper" style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 20px' }}>
      <div
        className="rounded-card border"
        style={{
          maxWidth: 440,
          width: '100%',
          backgroundColor: 'var(--bg-elev)',
          borderColor: 'var(--line)',
          padding: '28px 24px',
          textAlign: 'center',
        }}
      >
        <div className="av-eyebrow text-accent-ink mb-2">Almost there</div>
        <h1
          className="font-display text-ink"
          style={{ fontSize: 24, fontWeight: 500, letterSpacing: '-0.005em', lineHeight: 1.2 }}
        >
          Your provider's page isn't set up yet.
        </h1>
        <p className="text-sm text-ink-2 mt-3">
          {providerBusinessName
            ? <>Reach out to <strong className="text-ink">{providerBusinessName}</strong> directly to book your next session, or check back soon.</>
            : <>Reach out to your provider directly to book your next session, or check back soon.</>}
        </p>
        <div className="mt-6 space-y-2">
          <Link
            to="/my-bookings"
            className="inline-block w-full text-center rounded-btn"
            style={{
              minHeight: 44,
              padding: '11px 16px',
              backgroundColor: 'var(--cta-bg)',
              color: 'var(--cta-fg)',
              fontFamily: 'var(--font-sans)',
              fontSize: 14,
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            See my bookings
          </Link>
          <button
            type="button"
            onClick={handleSignOut}
            className="inline-block w-full text-center rounded-btn"
            style={{
              minHeight: 44,
              padding: '11px 16px',
              backgroundColor: 'transparent',
              color: 'var(--ink-2)',
              border: '1px solid var(--line)',
              fontFamily: 'var(--font-sans)',
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
